import type { AmountFees, Fee } from '@hero/schema';

/**
 * Sum the per-`calcType` rollups for a list of applied fees.
 *
 * Pure mirror of `AmountService.calcFees` (`apps/backend/src/app/amount/amount.service.ts`).
 * Field-for-field identical output is required — the equivalence spec
 * in `computeAmountPreview.spec.ts` enforces this.
 *
 * Rollups:
 *   - `added`      = Σ amount where `calcType === 'add_to_subtotal'`
 *   - `subtracted` = Σ amount where `calcType === 'subtract_from_subtotal'`
 *   - `realized`   = Σ amount for everything else (`'realized'` or unknown)
 *   - `all`        = Σ amount across every applied fee
 *   - `total`      = `added - subtracted` (the net effect on subtotal)
 *
 * The original `applied[]` array is preserved unchanged — fee amounts
 * are NOT rounded here (mirroring the source method, which rounds only
 * via `roundAll` and never touches per-fee amounts).
 */
export function computeFeeTotals(applied: ReadonlyArray<Fee>): AmountFees {
  let all = 0;
  let added = 0;
  let realized = 0;
  let subtracted = 0;

  for (const fee of applied) {
    all += fee.amount;
    if (fee.calcType === 'add_to_subtotal') {
      added += fee.amount;
    } else if (fee.calcType === 'subtract_from_subtotal') {
      subtracted += fee.amount;
    } else {
      realized += fee.amount;
    }
  }

  return {
    applied: [...applied],
    all,
    added,
    realized,
    subtracted,
    total: added - subtracted,
  };
}
